import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, Clock, Users, Plus, Edit2, Trash2, 
  Video, ExternalLink, ChevronLeft, ChevronRight,
  MapPin, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '/api';

export default function CalendarManagement() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);

  useEffect(() => {
    fetchLiveLessons();
  }, []);

  const fetchLiveLessons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/instructor/lessons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only live lessons
      const liveLessons = (response.data || []).filter(l => l.lesson_type === 'live');
      setLessons(liveLessons);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      toast.error(isEnglish ? 'Failed to load lessons' : 'Dersler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const updateLessonLink = async () => {
    if (!editingLesson) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/api/lessons/${editingLesson.id}`,
        { live_stream_url: editingLesson.live_stream_url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(isEnglish ? 'Link updated' : 'Link güncellendi');
      setShowEditModal(false);
      setEditingLesson(null);
      fetchLiveLessons();
    } catch (error) {
      console.error('Error updating lesson:', error);
      toast.error(isEnglish ? 'Failed to update' : 'Güncellenemedi');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    // Add empty days for padding
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add actual days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getLessonsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return lessons.filter(l => l.live_date === dateStr);
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const monthNames = isEnglish 
    ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    : ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

  const dayNames = isEnglish 
    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    : ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const days = getDaysInMonth(currentMonth);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-8 px-4 md:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isEnglish ? 'Total Live Classes' : 'Toplam Canlı Ders'}</p>
              <p className="text-2xl font-bold">{lessons.length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isEnglish ? 'With Link' : 'Linki Var'}</p>
              <p className="text-2xl font-bold text-green-600">
                {lessons.filter(l => l.live_stream_url).length}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <XCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isEnglish ? 'Needs Link' : 'Link Bekliyor'}</p>
              <p className="text-2xl font-bold text-yellow-600">
                {lessons.filter(l => !l.live_stream_url).length}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isEnglish ? 'This Week' : 'Bu Hafta'}</p>
              <p className="text-2xl font-bold text-blue-600">
                {lessons.filter(l => {
                  if (!l.live_date) return false;
                  const lessonDate = new Date(l.live_date);
                  const today = new Date();
                  const weekLater = new Date(today);
                  weekLater.setDate(today.getDate() + 7);
                  return lessonDate >= today && lessonDate <= weekLater;
                }).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {isEnglish ? 'Class Calendar' : 'Ders Takvimi'}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-medium min-w-[150px] text-center">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Day Headers */}
            <div className="grid grid-cols-7 mb-2">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const dayLessons = getLessonsForDate(day);
                const hasLessons = dayLessons.length > 0;
                
                return (
                  <div
                    key={index}
                    className={`min-h-[80px] p-1 border rounded-lg cursor-pointer transition-colors
                      ${!day ? 'bg-gray-50' : 'hover:bg-gray-50'}
                      ${isToday(day) ? 'border-purple-500 bg-purple-50' : ''}
                      ${isPast(day) && day ? 'opacity-50' : ''}
                      ${selectedDate?.toDateString() === day?.toDateString() ? 'ring-2 ring-purple-500' : ''}
                    `}
                    onClick={() => day && setSelectedDate(day)}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium ${isToday(day) ? 'text-purple-600' : ''}`}>
                          {day.getDate()}
                        </div>
                        {hasLessons && (
                          <div className="mt-1 space-y-1">
                            {dayLessons.slice(0, 2).map((lesson) => (
                              <div
                                key={lesson.id}
                                className={`text-xs p-1 rounded truncate ${
                                  lesson.live_stream_url 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}
                              >
                                {lesson.live_time} - {lesson.title?.substring(0, 10)}...
                              </div>
                            ))}
                            {dayLessons.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{dayLessons.length - 2} {isEnglish ? 'more' : 'daha'}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Details / Upcoming Classes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {selectedDate 
                ? formatDate(selectedDate) 
                : (isEnglish ? 'Upcoming Classes' : 'Yaklaşan Dersler')
              }
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDate ? (
              <div className="space-y-3">
                {getLessonsForDate(selectedDate).length > 0 ? (
                  getLessonsForDate(selectedDate).map((lesson) => (
                    <div key={lesson.id} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{lesson.title}</h4>
                          <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" />
                            {lesson.live_time} ({lesson.duration || 60} dk)
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingLesson(lesson);
                            setShowEditModal(true);
                          }}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      {lesson.live_stream_url ? (
                        <div className="mt-2 flex items-center gap-2">
                          <Badge className="bg-green-100 text-green-700">
                            <Video className="h-3 w-3 mr-1" />
                            {isEnglish ? 'Link Ready' : 'Link Hazır'}
                          </Badge>
                          <a
                            href={lesson.live_stream_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-purple-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {isEnglish ? 'Open' : 'Aç'}
                          </a>
                        </div>
                      ) : (
                        <Badge className="mt-2 bg-yellow-100 text-yellow-700">
                          <XCircle className="h-3 w-3 mr-1" />
                          {isEnglish ? 'Needs Link' : 'Link Gerekli'}
                        </Badge>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">
                    {isEnglish ? 'No classes on this day' : 'Bu gün ders yok'}
                  </p>
                )}
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setSelectedDate(null)}
                >
                  {isEnglish ? 'Show All Upcoming' : 'Tüm Yaklaşan Dersleri Göster'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {lessons
                  .filter(l => {
                    if (!l.live_date) return false;
                    const lessonDate = new Date(l.live_date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return lessonDate >= today;
                  })
                  .sort((a, b) => new Date(a.live_date) - new Date(b.live_date))
                  .slice(0, 10)
                  .map((lesson) => (
                    <div key={lesson.id} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-sm">{lesson.title}</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(lesson.live_date)} - {lesson.live_time}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingLesson(lesson);
                            setShowEditModal(true);
                          }}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                      </div>
                      {!lesson.live_stream_url && (
                        <Badge className="mt-2 bg-yellow-100 text-yellow-700 text-xs">
                          {isEnglish ? 'Add meeting link' : 'Toplantı linki ekleyin'}
                        </Badge>
                      )}
                    </div>
                  ))
                }
                
                {lessons.filter(l => {
                  if (!l.live_date) return false;
                  return new Date(l.live_date) >= new Date();
                }).length === 0 && (
                  <p className="text-center text-gray-500 py-8">
                    {isEnglish ? 'No upcoming classes' : 'Yaklaşan ders yok'}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Link Modal */}
      {showEditModal && editingLesson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {isEnglish ? 'Edit Meeting Link' : 'Toplantı Linkini Düzenle'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <p className="font-medium">{editingLesson.title}</p>
                <p className="text-sm text-gray-500">
                  {formatDate(editingLesson.live_date)} - {editingLesson.live_time}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  {isEnglish ? 'Meeting Link (Zoom/Google Meet)' : 'Toplantı Linki (Zoom/Google Meet)'}
                </label>
                <Input
                  value={editingLesson.live_stream_url || ''}
                  onChange={(e) => setEditingLesson({...editingLesson, live_stream_url: e.target.value})}
                  placeholder="https://zoom.us/j/..."
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowEditModal(false);
                  setEditingLesson(null);
                }}>
                  {isEnglish ? 'Cancel' : 'İptal'}
                </Button>
                <Button onClick={updateLessonLink}>
                  {isEnglish ? 'Save' : 'Kaydet'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
